import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Grid,
} from '@mui/material';
import katex from 'katex';
import 'katex/dist/katex.min.css';

import { CoordinateGrid } from '../coordinateGrid';
import {
  generateGraphVariations,
  polynomialsToCurveElements,
  evaluatePolynomialAt,
  parseFraction,
  equationToLatex,
} from './polynomialGraphUtils';
import { parseInputWithLabel } from '../shared/inputLabels';

/**
 * Student component for answering polynomial graph questions
 *
 * Props:
 * - config: PolynomialGraphConfig
 * - value: StudentAnswer (current answer state)
 * - onChange: (answer) => void
 * - disabled: boolean (after submission)
 */
const PolynomialGraphStudent = ({
  config,
  value = {},
  onChange,
  disabled = false,
  showCorrectness = false,
}) => {
  const [step, setStep] = useState(value?.step2?.selectedGraphIndex !== undefined ? 2 : 1);
  const [intercepts, setIntercepts] = useState(value?.step1?.intercepts?.map(String) || []);
  const [validationError, setValidationError] = useState('');
  const [selectedGraph, setSelectedGraph] = useState(value?.step2?.selectedGraphIndex ?? null);
  const [parseError, setParseError] = useState('');

  const { parsedPolynomial, interceptType, grid, interceptTolerance = 0.01 } = config || {};

  // Generate variations (using stored seed for consistency)
  // Returns { variations, error } to avoid side effects in useMemo
  const variationsResult = useMemo(() => {
    if (!parsedPolynomial) return { variations: [], error: '' };
    try {
      const { variations } = generateGraphVariations(
        parsedPolynomial,
        interceptType,
        config.variationSeed
      );
      return { variations, error: '' };
    } catch (e) {
      console.error('Failed to generate variations:', e);
      return { variations: [], error: 'Error loading question. Please contact your instructor.' };
    }
  }, [parsedPolynomial, interceptType, config?.variationSeed]);

  const variations = variationsResult.variations;

  // One shared y-compression across the variation set — per-curve scaling drew
  // curves with the same true y-intercept at different heights.
  const curveElements = useMemo(
    () => polynomialsToCurveElements(variations.map((v) => v.polynomial), grid),
    [variations, grid]
  );

  // Sync error state from useMemo result (proper side effect handling)
  useEffect(() => {
    setParseError(variationsResult.error);
  }, [variationsResult.error]);

  // Determine expected number of intercept inputs
  const expectedInterceptCount = interceptType === 'x'
    ? parsedPolynomial?.factors?.length || 2
    : 1;

  // Handle intercept input change
  const handleInterceptChange = useCallback((index, inputValue) => {
    const newIntercepts = [...intercepts];
    newIntercepts[index] = inputValue;
    setIntercepts(newIntercepts);
    setValidationError('');
  }, [intercepts]);

  // Validate intercepts and move to step 2
  const handleValidateIntercepts = useCallback(() => {
    // Parse student inputs using parseInputWithLabel (preserves typed form)
    const parsedInputs = intercepts
      .map(v => parseInputWithLabel(v))
      .filter(p => p.valid);
    const studentValues = parsedInputs.map(p => p.value);
    const studentLabels = parsedInputs.map(p => p.display);

    if (studentValues.length !== expectedInterceptCount) {
      setValidationError(`Please enter ${expectedInterceptCount} intercept value(s). You can use fractions like "1/2".`);
      return;
    }

    // Validate against correct values
    let isCorrect = false;

    if (interceptType === 'x') {
      // X-intercepts: compare roots
      const correctRoots = parsedPolynomial.factors
        .map(f => f.root)
        .sort((a, b) => a - b);
      const sortedStudent = [...studentValues].sort((a, b) => a - b);

      isCorrect = correctRoots.length === sortedStudent.length &&
        correctRoots.every((r, i) =>
          Math.abs(r - sortedStudent[i]) <= interceptTolerance
        );
    } else {
      // Y-intercept: compare f(0)
      const correctYInt = evaluatePolynomialAt(parsedPolynomial, 0);
      isCorrect = Math.abs(correctYInt - studentValues[0]) <= interceptTolerance;
    }

    if (isCorrect) {
      setStep(2);
      setValidationError('');
      // Update answer with step 1 completion
      onChange({
        ...value,
        step1: {
          intercepts: studentValues,
          interceptLabels: studentLabels,
          timestamp: new Date().toISOString(),
        },
      });
    } else {
      setValidationError('Incorrect intercept value(s). Please check your calculations.');
    }
  }, [intercepts, expectedInterceptCount, interceptType, parsedPolynomial,
      interceptTolerance, value, onChange]);

  // Handle graph selection
  const handleGraphSelect = useCallback((index) => {
    if (disabled) return;

    setSelectedGraph(index);
    onChange({
      ...value,
      step1: value.step1 || { intercepts: intercepts.map(v => parseFraction(v)) },
      step2: {
        selectedGraphIndex: index,
        timestamp: new Date().toISOString(),
      },
    });
  }, [disabled, value, intercepts, onChange]);

  // ERROR BOUNDARY: Show error message if config is malformed
  if (parseError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{parseError}</Alert>
      </Box>
    );
  }

  // Guard against missing config
  if (!config || !parsedPolynomial) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Question configuration is missing.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Equation Display */}
      <Paper sx={{ p: 2, bgcolor: 'primary.light' }}>
        <Typography variant="h6" align="center">
          <span dangerouslySetInnerHTML={{
            __html: katex.renderToString(equationToLatex(config.equation), { throwOnError: false })
          }} />
        </Typography>
      </Paper>

      {/* Step 1: Intercept Input */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Step 1: Enter the {interceptType === 'x' ? 'x-intercepts' : 'y-intercept'}
        </Typography>

        {interceptType === 'x' ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Find all values of x where the graph crosses or touches the x-axis.
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Calculate f(0) to find where the graph crosses the y-axis.
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          {Array.from({ length: expectedInterceptCount }).map((_, index) => (
            <TextField
              key={index}
              label={interceptType === 'x'
                ? `x-intercept ${index + 1}`
                : 'y-intercept'}
              value={intercepts[index] || ''}
              onChange={(e) => handleInterceptChange(index, e.target.value)}
              disabled={step === 2 || disabled}
              placeholder="e.g., -3, 1/2, 2.5"
              sx={{ width: 150 }}
            />
          ))}
        </Box>

        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        {step === 1 && !disabled && (
          <Button
            variant="contained"
            onClick={handleValidateIntercepts}
            disabled={intercepts.filter(v => v?.trim?.()).length < expectedInterceptCount}
          >
            Check & Continue
          </Button>
        )}

        {step === 2 && (
          <Alert severity="success">
            Correct! Now select the graph that matches this polynomial.
          </Alert>
        )}
      </Paper>

      {/* Step 2: Graph Selection */}
      {step === 2 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Step 2: Select the correct graph
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            All graphs pass through the intercept(s) you found.
            Choose the one that correctly represents the given polynomial.
          </Typography>

          <Grid container spacing={2}>
            {variations.map((variation, index) => {
              const isSelected = selectedGraph === index;
              const showResult = showCorrectness && isSelected;
              const isCorrectChoice = showResult && variation.isCorrect;
              const isWrongChoice = showResult && !variation.isCorrect;
              const showCorrectHint = showCorrectness && selectedGraph !== null && !isSelected && variation.isCorrect;

              let borderStyle = '1px solid grey';
              if (isCorrectChoice || showCorrectHint) borderStyle = '3px solid green';
              else if (isWrongChoice) borderStyle = '3px solid red';
              else if (isSelected) borderStyle = '3px solid blue';

              let bgColor = 'background.paper';
              if (isCorrectChoice || showCorrectHint) bgColor = 'success.light';
              else if (isWrongChoice) bgColor = 'error.light';

              return (
                <Grid item xs={6} key={variation.label}>
                  <Paper
                    onClick={() => handleGraphSelect(index)}
                    sx={{
                      p: 1,
                      cursor: disabled ? 'default' : 'pointer',
                      border: borderStyle,
                      bgcolor: bgColor,
                      '&:hover': disabled ? {} : {
                        borderColor: 'primary.main',
                        bgcolor: showResult ? undefined : 'action.hover',
                      },
                    }}
                  >
                    <Typography variant="subtitle2" align="center">
                      Graph {variation.label}
                      {isCorrectChoice && ' (Correct!)'}
                      {isWrongChoice && ' (Incorrect)'}
                      {showCorrectHint && ' (Correct)'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <CoordinateGrid
                        config={grid}
                        elements={[curveElements[index]]}
                        pendingPoints={[]}
                        mode="point"
                        readOnly={true}
                        width={180}
                        height={180}
                      />
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default PolynomialGraphStudent;
